import { TableColumnsProp } from 'src/app/shared/models/tables.model';
import {
  RateScaleAccessorialAgentChargeSheetConfig,
  RateScaleSummaryRow,
  WeightBasedChargeConfig,
} from './rate-scales.model';
import { FormConfig } from 'src/app/shared/models/dynamic.model';
import {
  BulkDeleteItemNames,
  gridMessages,
  GridColumnsStorageNames,
  MessageTemplates,
  rateScaleErrorMessages,
  searchDataParam,
} from 'src/app/shared/utility/constants';
import { DataTableConfig } from 'src/app/shared/components/data-table';

const isSelectedAndDefaultView = {
  isSelected: true,
  defaultView: true,
};

export const rateScalesSummaryTableColumnConfig: TableColumnsProp[] = [
  {
    ...isSelectedAndDefaultView,
    key: 'rateScale',
    label: 'Rate Scale',
    defaultPrintWidth: 200,
  },
  {
    ...isSelectedAndDefaultView,
    key: 'destination',
    label: 'Destination',
    defaultPrintWidth: 100,
  },
  {
    ...isSelectedAndDefaultView,
    key: 'agent',
    label: 'Agent',
    defaultPrintWidth: 300,
  },
];

export const rateScaleDataTableConfig: DataTableConfig<RateScaleSummaryRow> = {
  rowIdField: 'rateScaleId',
  entityName: BulkDeleteItemNames.rateScales,
  storageKey: GridColumnsStorageNames.rateScales,
  defaultLockedColumn: 'rateScale',
  features: {
    selection: true,
    pagination: true,
    sorting: true,
    columnReorder: true,
    selectAllAcrossPages: true,
    columnCustomization: true,
  },
  messages: {
    noSearch: gridMessages.noRateScaleSearch,
    noMatch: gridMessages.noRateScaleMatch,
    allPageSelected: MessageTemplates.AllItemsSelected,
    selectAll: MessageTemplates.SelectAllItems,
  },
  defaultPagination: {
    pageSize: 10,
    sortField: 'rateScaleId',
    sortDirection: 'desc',
  },
  columns: [
    {
      key: 'rateScale',
      label: 'Rate Scale',
      isSelected: true,
      defaultView: true,
      sortable: true,
      isHyperlink: true,
      defaultPrintWidth: 200,
    },
    {
      key: 'destination',
      label: 'Destination',
      isSelected: true,
      defaultView: true,
      sortable: true,
      defaultPrintWidth: 100,
    },
    {
      key: 'agent',
      label: 'Agent',
      isSelected: true,
      defaultView: true,
      sortable: true,
      defaultPrintWidth: 300,
    },
  ],
};

export const rateScaleInfoFormConfig: FormConfig = {
  fields: [
    {
      id: 'rateScaleId',
      type: 'extra',
    },
    {
      id: 'rateScaleName',
      type: 'input',
      required: true,
      label: 'Rate Scale',
      requiredError: 'Rate Scale is required',
      allowAlphaNumericWithSpaceNoSpecialCharacter: true,
      maxValue: 126,
      isCheckForMaxValue: true,
      noInitialWhiteSpace: true,
    },
    {
      id: 'destination',
      type: 'input',
      label: 'Destination',
      required: true,
      requiredError: 'Destination is mandatory',
    },
    {
      id: 'agentId',
      type: 'input',
      label: 'Agent',
      required: false,
    },
    {
      id: 'description',
      label: 'Description',
      type: 'text',
      required: false,
      maxValue: 500,
      isCheckForMaxValue: true,
      noInitialWhiteSpace: true,
    },
    {
      id: 'information',
      type: 'text',
      label: 'Information',
      required: false,
      maxValue: 500,
      isCheckForMaxValue: true,
      noInitialWhiteSpace: true,
    },
  ],
};

export const accessorialChargesManualEntryTableConfig: TableColumnsProp[] = [
  {
    key: 'agentChargeCode',
    label: 'Agent Charge Code',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'agentChargeDescription',
    label: 'Agent Charge Description',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'ibcChargeCode',
    label: 'IBC Charge Code',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'chargeType',
    label: 'Charge Type',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'passThrough',
    label: 'Pass Through',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'addAgentMarkupCharges',
    label: 'Markup (%)',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'flatFee',
    label: 'Flat Fee ($)',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'chargeAmount',
    ...isSelectedAndDefaultView,
    label: 'Charge Amount ($)',
  },
];

export interface AccessorialUploadSheetRow {
  agentChargeCode: string;
  agentChargeDescription: string;
  ibcChargeCode: string;
  chargeType: string;
  passThrough: string;
  addAgentMarkupCharges: string;
  flatFee: string;
  chargeAmount: string;
}

export function buildAccessorialUploadSheetDataTableConfig(): DataTableConfig<AccessorialUploadSheetRow> {
  return {
    rowIdField: 'agentChargeCode',
    entityName: 'Accessorial Charges',
    storageKey: GridColumnsStorageNames.rsAccessorialChartUpload,
    columns: accessorialChargesManualEntryTableConfig.map(col => ({
      key: col.key,
      label: col.label,
      isSelected: col.isSelected ?? true,
      defaultView: col.defaultView ?? true,
      sortable: false,
    })),
    features: {
      selection: false,
      pagination: false,
      sorting: false,
      columnReorder: false,
      selectAllAcrossPages: false,
      columnCustomization: false,
      hideEmptyMessage: true,
    },
    messages: {
      noSearch: '',
      noMatch: '',
      allPageSelected: (entity, count) => `All ${count} ${entity} selected.`,
      selectAll: (entity, total) => `Select all ${total} ${entity}`,
    },
  };
}

export const weightBasedChargesTableColumsData = [
  {
    label: 'Outlying Rate',
    cssClass: 'outlying-header',
    hasFilter: false,
  },
  {
    label: 'Agent Service Type',
    cssClass: 'agent-service-header',
    hasFilter: true,
    filterMenuRef: 'agentServiceFilterMenu',
  },
  {
    label: 'IBC Service Type',
    cssClass: 'ibc-service-header',
    hasFilter: true,
    filterMenuRef: 'ibcServiceFilterMenu',
  },
  {
    label: 'Weight (lbs)',
    cssClass: 'weight-header',
    hasFilter: false,
  },
  {
    label: 'Charge Amount ($)',
    cssClass: 'charge-amount-header',
    hasFilter: false,
  },
  {
    label: 'Actions',
    cssClass: 'actions-header',
    hasFilter: false,
  },
];

/** Destination countries treated as Domestic; every other country is International. */
export const WeightBasedDomesticCountryCodes = ['US', 'CA'];

export const weightBasedChargesZonesTableColumsData = [
  {
    label: 'Outlying Rate',
    cssClass: 'outlying-header',
    hasFilter: false,
  },
  {
    label: 'Agent Service Type',
    cssClass: 'agent-service-header',
    hasFilter: true,
    filterMenuRef: 'agentServiceFilterMenu',
  },
  {
    label: 'IBC Service Type',
    cssClass: 'ibc-service-header',
    hasFilter: true,
    filterMenuRef: 'ibcServiceFilterMenu',
  },
  {
    label: 'Weight (lbs)',
    cssClass: 'weight-header',
    hasFilter: false,
  },
];

export const accessorialChargesManualEntryFormConfig: FormConfig = {
  fields: [
    {
      id: 'agentChargeCode',
      type: 'input',
      requiredError: 'Agent Charge Code is required',
    },
    {
      type: 'input',
      id: 'agentChargeDescription',
      noInitialWhiteSpace: true,
      requiredError: 'Agent Charge Description is required',
    },
    {
      type: 'input',
      id: 'ibcChargeCode',
      required: true,
      requiredError: 'IBC Charge Code is required',
    },
    {
      id: 'chargeType',
      type: 'input',
      requiredError: 'Charge Type is required',
    },
    {
      type: 'input',
      id: 'passThrough',
    },
    {
      type: 'input',
      id: 'addAgentMarkupCharges',
      allowNumericWithDecimal: true,
      noInitialWhiteSpace: true,
    },
    {
      id: 'flatFee',
      type: 'input',
      allowNumericWithDecimal: true,
      noInitialWhiteSpace: true,
    },
    {
      type: 'input',
      id: 'chargeAmount',
      allowNumericWithDecimal: true,
      noInitialWhiteSpace: true,
      required: true,
      requiredError: 'Charge Amount ($) is required',
    },
  ],
};

export const accessorialAgentChargeSheetData: RateScaleAccessorialAgentChargeSheetConfig[] =
  [
    {
      agentChargeCode: 'V8982',
      agentChargeDescription: 'lorem ipsum',
      ibcChargeCode: 'COR',
      chargeType: 'MSC',
      passThrough: 'No',
      flatFee: '',
      chargeAmount: '10',
      addAgentMarkupCharges: '10',
    },
    {
      passThrough: 'Yes',
      agentChargeDescription: 'lorem ipsum',
      chargeType: 'MSC',
      chargeAmount: '20',
      ibcChargeCode: 'SPS',
      flatFee: '',
      agentChargeCode: 'V8981',
      addAgentMarkupCharges: '',
    },
    {
      agentChargeDescription: 'lorem ipsum',
      ibcChargeCode: 'DUT',
      flatFee: '',
      chargeAmount: '10',
      chargeType: 'DUT',
      agentChargeCode: 'V8983',
      passThrough: 'No',
      addAgentMarkupCharges: '',
    },
  ];

function generateZoneColumns(count: number): TableColumnsProp[] {
  const zones: TableColumnsProp[] = [];
  for (let i = 1; i <= count; i++) {
    zones.push({
      key: `zone${i}`,
      label: `Zone ${i}`,
      ...isSelectedAndDefaultView,
    });
  }
  return zones;
}

export const getWeightBasedChargesTableConfig = (
  zoneCount: number
): TableColumnsProp[] => {
  const outlyingColumn: TableColumnsProp = {
    ...isSelectedAndDefaultView,
    label: 'Outlying Rate',
    key: 'outlying',
  };
  if (zoneCount === 0) {
    return [
      outlyingColumn,
      {
        ...isSelectedAndDefaultView,
        label: 'Agent Service Type',
        key: 'agentServiceType',
      },
      {
        key: 'ibcServiceType',
        ...isSelectedAndDefaultView,
        label: 'IBC Service Type',
      },
      {
        ...isSelectedAndDefaultView,
        label: 'Weight (lbs)',
        key: 'weight',
      },
      {
        ...isSelectedAndDefaultView,
        label: 'Charge Amount ($)',
        key: 'chargeAmount',
      },
      {
        ...isSelectedAndDefaultView,
        label: 'Actions',
        key: 'actions',
      },
    ];
  } else {
    return [
      outlyingColumn,
      {
        ...isSelectedAndDefaultView,
        label: 'Agent Service Type',
        key: 'agentServiceType',
      },
      {
        key: 'ibcServiceType',
        ...isSelectedAndDefaultView,
        label: 'IBC Service Type',
      },
      {
        ...isSelectedAndDefaultView,
        label: 'Weight (lbs)',
        key: 'weight',
      },
      {
        ...isSelectedAndDefaultView,
        label: 'Zone Type',
        key: 'zoneType',
      },
      ...generateZoneColumns(zoneCount),
      {
        ...isSelectedAndDefaultView,
        label: 'Actions',
        key: 'actions',
      },
    ];
  }
};

export const getWeightBasedChargesManualEntryFormConfig = (
  zoneCount: number
): FormConfig => {
  const baseFields: any = [
    {
      id: 'agentServiceType',
      type: 'input',
      requiredError: 'Agent Service Type is required',
      noInitialWhiteSpace: true,
    },
    {
      id: 'ibcServiceType',
      type: 'input',
      required: true,
      requiredError: 'IBC Service Type is required',
      noInitialWhiteSpace: true,
    },
    {
      id: 'weight',
      type: 'input',
      allowNumericWithDecimal: true,
      noInitialWhiteSpace: true,
      required: true,
      requiredError: 'Weight is required',
      maxValue: 126,
      isCheckForMaxValue: true,
    },
  ];

  const chargeAmountField: any = {
    id: 'chargeAmount',
    type: 'input',
    allowNumericWithDecimal: true,
    noInitialWhiteSpace: true,
    required: true,
    requiredError: 'Charge Amount ($) is required',
  };

  const zoneFields: any[] = [];

  for (let i = 1; i <= zoneCount; i++) {
    zoneFields.push({
      id: `zone${i}`,
      type: 'input',
      allowNumeric: true,
      noInitialWhiteSpace: true,
      required: true,
      requiredError: `Zone${i} is required`,
    });
  }

  return {
    fields: [
      ...baseFields,
      ...(zoneCount === 0 ? [chargeAmountField] : zoneFields),
    ],
  };
};

export const weightBasedChargesData: WeightBasedChargeConfig[] = [
  {
    ibcServiceType: 'ST - Standard',
    weight: 100,
    agentServiceType: 'ST - Basic Standard',
    chargeAmount: 100,
    zone1: 10,
    zone2: 20,
    zone3: 30,
  },
  {
    ibcServiceType: 'E - Express',
    weight: 200,
    agentServiceType: 'EXP - Express',
    chargeAmount: 100,
    zone1: 15,
    zone2: 25,
    zone3: 35,
  },
  {
    ibcServiceType: 'ST - Standard',
    weight: 300,
    agentServiceType: 'ST - Basic Standard',
    chargeAmount: 100,
    zone1: 12,
    zone2: 22,
    zone3: 32,
  },
];

export const defautRateScalePagination = {
  sortingOrder: searchDataParam.desc,
  sortingField: searchDataParam.rateScaleId,
  fromIndex: 0,
  toIndex: 10,
};

export const RsAccessorialChargeColumnList = [
  'agentChargeCode',
  'agentChargeDescription',
  'ibcChargeCode',
  'chargeType',
  'passThrough',
  'addAgentMarkupCharges',
  'flatFee',
  'chargeAmount',
];
export const AccessorialHEADERS = [
  'AgentChargeCode',
  'AgentChargeDescription',
  'IBCChargeCode',
  'ChargeType',
  'PassThrough',
  'Markup%',
  'FlatFee',
  'ChargeAmount',
];

export const RsWeightBasedStaticColumnKeys = [
  'outlying',
  'agentServiceType',
  'ibcServiceType',
  'weight',
];

export const WeightBasedStaticHeaders = [
  'Outlying Rate',
  'Agent Service Type',
  'IBC Service Type',
  'Weight',
];

export const RsWeightBasedZoneColumnKeys = [
  'zone1',
  'zone2',
  'zone3',
  'zone4',
  'zone5',
];

export const WeightBasedZoneHeaders = [
  'Zone1',
  'Zone2',
  'Zone3',
  'Zone4',
  'Zone5',
];

export const RsWeightBasedChargeColumnList = [
  ...RsWeightBasedStaticColumnKeys,
  ...RsWeightBasedZoneColumnKeys,
];

export const WeightBasedheaders = [
  ...WeightBasedStaticHeaders,
  ...WeightBasedZoneHeaders,
];

export function getWeightBasedChargeSheetZoneCount(
  agentZonesCount: number
): number {
  return Number(agentZonesCount) > 0 ? Number(agentZonesCount) : 1;
}

export const getWeightBasedDownloadColumnConfig = (
  zoneCount: number
): { headers: string[]; columnKeys: string[] } => ({
  headers: getWeightBasedUploadExpectedHeaders(zoneCount),
  columnKeys: [
    ...RsWeightBasedStaticColumnKeys,
    ...RsWeightBasedZoneColumnKeys.slice(0, zoneCount),
  ],
});

export const WeightBasedUploadHeaderAliases: Readonly<
  Record<string, string>
> = {
  'Outlying Rate': 'OutlyingRate',
  'Agent Service Type': 'AgentServiceType',
  'IBC Service Type': 'IBCServiceType',
  'Weight (lbs)': 'Weight',
  'Charge Amount ($)': 'Zone1',
};

export const WeightBasedUploadStaticHeaders = [
  'OutlyingRate',
  'AgentServiceType',
  'IBCServiceType',
  'Weight',
];

export function normalizeWeightBasedUploadHeaders(headers: string[]): string[] {
  return headers.map(header => {
    const trimmed = (header ?? '').trim();
    if (WeightBasedUploadHeaderAliases[trimmed]) {
      return WeightBasedUploadHeaderAliases[trimmed];
    }
    const zoneMatch = trimmed.match(/^Zone\s*(\d+)$/i);
    if (zoneMatch) {
      return `Zone${zoneMatch[1]}`;
    }
    return trimmed;
  });
}

export function getWeightBasedUploadExpectedHeaders(
  zoneCount: number
): string[] {
  const zones =
    zoneCount > 0
      ? WeightBasedZoneHeaders.slice(0, zoneCount)
      : ['Zone1'];
  return [...WeightBasedUploadStaticHeaders, ...zones];
}

export interface ChargeSheetHeaderValidationOptions {
  agentZonesCount?: number;
}

export function validateChargeSheetHeaders(
  actualHeaders: string[],
  expectedHeaders: string[],
  options?: ChargeSheetHeaderValidationOptions
): string | null {
  if (!Array.isArray(actualHeaders) || actualHeaders.length === 0) {
    return rateScaleErrorMessages.headerErrorEmpty;
  }

  const mismatches: string[] = [];
  const agentZonesCount = options?.agentZonesCount;

  for (let i = 0; i < expectedHeaders.length; i++) {
    const expected = expectedHeaders[i];
    const actual = (actualHeaders[i] ?? '').trim();
    if (actual !== expected) {
      mismatches.push(
        rateScaleErrorMessages.headerMismatchAtColumn(
          i + 1,
          actual || '(empty)',
          expected
        )
      );
    }
  }

  for (let i = expectedHeaders.length; i < actualHeaders.length; i++) {
    const extra = (actualHeaders[i] ?? '').trim();
    if (!extra) {
      continue;
    }

    const isExtraZoneHeader = /^Zone\d+$/i.test(extra);
    if (agentZonesCount !== undefined && isExtraZoneHeader) {
      mismatches.push(
        rateScaleErrorMessages.headerExtraZoneColumn(
          i + 1,
          extra,
          agentZonesCount
        )
      );
      continue;
    }

    mismatches.push(
      rateScaleErrorMessages.headerUnexpectedColumn(
        i + 1,
        extra,
        expectedHeaders.length
      )
    );
  }

  if (mismatches.length === 0) {
    return null;
  }

  if (mismatches.length === 1) {
    return mismatches[0];
  }

  return rateScaleErrorMessages.headerMismatchSummary(mismatches);
}

export const copyRateScaleInfoFormConfig: FormConfig = {
  fields: [
    {
      id: 'rateScaleName',
      type: 'input',
      required: true,
      label: 'Rate Scale',
      requiredError: 'Rate Scale is required',
      allowAlphaNumericWithSpaceNoSpecialCharacter: true,
      maxValue: 126,
      isCheckForMaxValue: true,
      noInitialWhiteSpace: true,
    },
    {
      id: 'markup',
      type: 'input',
      label: 'Markup',
      requiredError: 'Mark Up is required',
      required: true,
      noInitialWhiteSpace: true,
    },
  ],
};

export const rateScaleNoWeightBasedChargesConfirmationButtons = [
  {
    name: 'Cancel',
    btnClass: 'ibc-medium-button ibc-secondary-default-button cursor',
  },
  {
    name: 'Proceed',
    btnClass: 'ibc-medium-button ibc-primary-default-button cursor',
  },
];
